import { api } from '@/lib/api-client'

// The shared `api` client unwraps the `{ status, message, data }` envelope in
// the response interceptor — `api.get<T>` resolves directly to `T`. These
// wrappers therefore type the *inner* `data` shape only.

// ── /entities ───────────────────────────────────────────────────────────────
// Picker source for the report builder. We deliberately re-wrap this here
// (rather than importing from `src/systems/entities/`) because ESLint module
// boundaries forbid system-to-system imports — every system owns its own
// API surface.

export interface ReportEntity {
  id: string
  name: string
}

export function listEntities(): Promise<{ entities: ReportEntity[] }> {
  return api.get<{ entities: ReportEntity[] }>('/entities')
}

// ── /entities/{id}/schedules ────────────────────────────────────────────────

export interface ReportSchedule {
  id: string
  name: string
  schedule_type: 'one_time' | 'recurring'
  start_date: string
  end_date: string
}

export function listSchedulesByEntity(entityId: string): Promise<ReportSchedule[]> {
  return api.get<ReportSchedule[]>(`/entities/${entityId}/schedules`)
}

// ── /schedules/{id} ─────────────────────────────────────────────────────────

export interface ReportScheduleCycle {
  cycle_number: number
  cycle_start: string
  cycle_end: string
  completed_count: number
  missed_count: number
  is_active: boolean
}

export interface ReportScheduleDetail {
  id: string
  name: string
  schedule_type: 'one_time' | 'recurring'
  cycles: ReportScheduleCycle[]
}

export function getScheduleDetail(scheduleId: string): Promise<ReportScheduleDetail> {
  return api.get<ReportScheduleDetail>(`/schedules/${scheduleId}`)
}

// ── /reports/survey-cycle ───────────────────────────────────────────────────
// The rich payload that powers the FIMMS Survey Report screen. The response
// root carries the cycle context (schedule_id / name / type, cycle_number,
// cycle_start, cycle_end) alongside the summary + per-row breakdowns.

export interface SurveyReportRow {
  site_id: string
  site_name: string
  mandal: string
  department_id: string
  department_name: string
  hod_name: string | null
  field_officer: string
  // `pending` only appears for a live (in-progress) cycle — a survey not yet
  // submitted. After the inspection window closes the backend's `mark-missed`
  // job flips still-unsubmitted rows to `missed`. NOT the same as issue-pending.
  survey_status: 'completed' | 'missed' | 'pending'
  submitted_at: string | null
  issues_raised: number
  critical: number
}

export interface SurveyReportFO {
  name: string
  sites: number
  completed: number
  missed: number
  // Survey-pending for this FO. Optional — present only on a live cycle.
  pending?: number
  issues_raised: number
  critical: number
}

export interface SurveyReportHOD {
  name: string
  department: string
  sites: number
  issues_received: number
  critical_received: number
  resolved: number
  closed: number
  pending: number
  closure_pct: number
}

export interface SurveyReportIssue {
  ref: string
  site_id: string
  section: string
  title: string
  severity: 'critical' | 'non_critical'
  // Lifecycle v2: report lambdas send the two-tier issue status only.
  status: 'open' | 'completed'
  raised_at: string
  resolved_at: string | null
  raised_by: string
}

export interface SurveyReportPayload {
  schedule_id: string
  schedule_name: string
  schedule_type: 'one_time' | 'recurring'
  cycle_number: number
  cycle_start: string
  cycle_end: string
  // True when this is a live, in-progress cycle (only present when the backend
  // reports the active cycle via `include_active=true`). Drives the amber
  // survey-pending visuals below.
  is_active?: boolean
  summary: {
    sites: number
    completed: number
    missed: number
    // Survey-pending: surveys not yet submitted (live cycles only). Distinct
    // from `issues_pending` (an issue not yet closed).
    pending?: number
    issues_raised: number
    issues_closed: number
    issues_pending: number
    critical_active: number
    generated_at: string
  }
  department_breakdown: Array<{
    department: string
    hod: string
    sites: number
    completed: number
    missed: number
    // Survey-pending for this department (live cycles only). `pending` below
    // keeps its original meaning: issue-pending (issues − resolved).
    pending_surveys?: number
    issues: number
    critical: number
    resolved: number
    pending: number
    resolution_pct: number
  }>
  section_pain_points: Array<{
    section: string
    issues: number
    critical: number
    non_critical: number
  }>
  rows: SurveyReportRow[]
  field_officers: SurveyReportFO[]
  hods: SurveyReportHOD[]
  issues: SurveyReportIssue[]
}

export function getSurveyReport(
  scheduleId: string,
  cycleNumber: number,
): Promise<SurveyReportPayload> {
  // `include_active=true` is always sent: harmless for finished cycles (the
  // backend ignores it and returns the unchanged 200), and required to get a
  // live 200 (instead of a 422) for the currently in-progress cycle.
  return api.get<SurveyReportPayload>(`/reports/survey-cycle`, {
    params: { schedule_id: scheduleId, cycle_number: cycleNumber, include_active: true },
  })
}

// ── /reports/period ─────────────────────────────────────────────
// Backend: docs/mock/BACKEND_ASKS_REPORTS_FULL.md "Ask 2"
export interface PeriodReportMandal {
  mandal: string
  issues: number
  critical: number
  pending: number
}
export interface PeriodReportSection {
  section: string
  issues: number
  critical: number
  non_critical: number
  pending: number
}
export interface PeriodReportFO {
  name: string
  sites: number
  completed: number
  missed: number
  issues: number
  critical: number
}
export interface PeriodReportHOD {
  name: string
  dept: string
  sites: number
  received: number
  resolved: number
  closed: number
  critical_received: number
}
export interface PeriodReportCycle {
  schedule_id: string
  schedule_name: string
  cycle_number: number
  cycle_start: string
  cycle_end: string
  completed: number
  missed: number
  issues: number
  critical: number
}
export interface PeriodReportIssue {
  ref: string
  site_name: string
  mandal: string
  section: string
  title: string
  severity: 'critical' | 'non_critical'
  // Lifecycle v2: report lambdas send the two-tier issue status only.
  status: 'open' | 'completed'
  raised_at: string
  resolved_at: string | null
  raised_by: string
}
export interface PeriodReportPayload {
  summary: {
    sites_surveyed: number
    missed: number
    issues_raised: number
    closed: number
    pending: number
    critical: number
  }
  mandal_distribution: PeriodReportMandal[]
  section_distribution: PeriodReportSection[]
  field_officers: PeriodReportFO[]
  hods: PeriodReportHOD[]
  cycles_in_window: PeriodReportCycle[]
  issues: PeriodReportIssue[]
}

export interface PeriodReportRequest {
  from: string // YYYY-MM-DD
  to: string // YYYY-MM-DD
  entityIds?: string[]
  deptIds?: string[]
  mandalIds?: string[]
}

export function getPeriodReport(req: PeriodReportRequest): Promise<PeriodReportPayload> {
  return api.get<PeriodReportPayload>('/reports/period', {
    params: {
      from: req.from,
      to: req.to,
      entity_ids: req.entityIds,
      dept_ids: req.deptIds,
      mandal_ids: req.mandalIds,
    },
  })
}

// ── /reports/site-dossier ───────────────────────────────────────
// Backend: docs/mock/BACKEND_ASKS_REPORTS_FULL.md "Ask 3"
export interface SiteDossierSite {
  id: string
  name: string
  address: string
  mandal: string
  lat: number
  long: number
  geofence_radius_meters: number
  department: { id: string; name: string }
  hod_name: string | null
  field_officer: string
}
export interface SiteDossierLifetimeStats {
  surveys: number
  issues: number
  completed: number
  open: number
  critical: number
}
export interface SiteDossierSeries {
  schedule_id: string
  schedule_name: string
  schedule_type: 'one_time' | 'recurring'
  cycles: {
    cycle_number: number
    status: 'completed' | 'missed'
    submitted_at: string | null
    issues: number
    critical: number
  }[]
}
export interface SiteDossierIssue {
  ref: string
  section: string
  title: string
  severity: 'critical' | 'non_critical'
  // Lifecycle v2: report lambdas send the two-tier issue status only.
  status: 'open' | 'completed'
  source_schedule_name: string
  source_cycle_number: number
  raised_at: string
  resolved_at: string | null
  raised_by: string
}
export interface SiteDossierEntry {
  site: SiteDossierSite
  lifetime_stats: SiteDossierLifetimeStats
  surveys_by_series: SiteDossierSeries[]
  issues: SiteDossierIssue[]
}
export interface SiteDossierPayload {
  sites: SiteDossierEntry[]
}

export interface SiteDossierRequest {
  siteIds: string[]
  from?: string | null
  to?: string | null
}

export function getSiteDossier(req: SiteDossierRequest): Promise<SiteDossierPayload> {
  return api.get<SiteDossierPayload>('/reports/site-dossier', {
    params: {
      site_ids: req.siteIds,
      from: req.from ?? undefined,
      to: req.to ?? undefined,
    },
  })
}

// ── /reports/checklist ──────────────────────────────────────────
// Collector checklist: one row per site, one column per checklist question,
// answers auto-filled from submitted surveys. The collector downloads the XLSX
// and phones the HOD / field officer to interrogate each Yes/No — which is why
// officer name + phone ride along on every row.

export interface ChecklistReportScheduleInfo {
  id: string
  name: string
  schedule_type: 'one_time' | 'recurring'
  entity: { id: string; name: string; short_code: string }
}

export interface ChecklistReportCycleInfo {
  cycle_number: number
  cycle_start: string
  cycle_end: string
  is_active: boolean
}

export interface ChecklistReportChecklist {
  id: string
  title: string
  /** Header text for the site column (e.g. "Name of the Hostel"). */
  site_column_header: string
  columns: string[]
  /**
   * Headers whose cells are ALWAYS blank for now — no form question captures
   * them yet. Rendered with a "not captured by the survey yet" hint, not an
   * error.
   */
  unmapped_columns: string[]
}

export interface ChecklistReportRow {
  sl_no: number
  site_id: string
  site_name: string
  mandal: string
  field_officer: string
  field_officer_phone: string
  survey_status: 'surveyed' | 'yet_to_be_surveyed'
  surveyed_at: string | null
  /** Index-aligned with `checklist.columns`; null = blank cell. */
  answers: (string | null)[]
}

export interface ChecklistReportPayload {
  schedule: ChecklistReportScheduleInfo
  cycle: ChecklistReportCycleInfo
  checklist: ChecklistReportChecklist
  summary: { sites: number; surveyed: number; yet_to_be_surveyed: number }
  rows: ChecklistReportRow[]
}

export function getChecklistReport(
  scheduleId: string,
  cycleNumber: number,
): Promise<ChecklistReportPayload> {
  // `include_active=true` always (same convention as getSurveyReport): the
  // collector's default view is the LATEST cycle even while it's in progress —
  // sites without a submission come back as "yet_to_be_surveyed".
  return api.get<ChecklistReportPayload>('/reports/checklist', {
    params: { schedule_id: scheduleId, cycle_number: cycleNumber, include_active: true },
  })
}

/** Query-string params for the XLSX variant — pass to `downloadFile('/reports/checklist', …)`. */
export function checklistXlsxParams(scheduleId: string, cycleNumber: number) {
  return {
    schedule_id: scheduleId,
    cycle_number: cycleNumber,
    include_active: true,
    format: 'xlsx',
  }
}

// ── /reports/completed-surveys ──────────────────────────────────
// Powers the quick-download Reports landing: a server-sorted, paginated
// list of report-ready completed survey cycles (most-recent first). Each
// row carries exactly what the download pipeline needs (schedule_id +
// cycle_number) so a click drops straight into GET /reports/survey-cycle
// — no client-side fan-out over /entities → /schedules → /schedules/{id}.

export interface CompletedSurveySeverity {
  critical: number
  non_critical: number
}

export interface CompletedSurvey {
  schedule_id: string
  schedule_name: string
  schedule_type: 'one_time' | 'recurring'
  entity_id: string
  entity_name: string
  cycle_number: number
  cycle_start: string
  cycle_end: string
  completed_count: number // sites completed in this cycle
  // Sites missed in this cycle. A REAL count even on a live cycle — a site's
  // own window can expire (daily mark-missed sweep) before the overall cycle
  // window ends, so don't assume 0 when is_active is true.
  missed_count: number
  // Surveys not yet submitted. Always 0 for a finished cycle; can be large
  // for a live one (window still open) — NOT the same as missed.
  pending_count: number
  // True only for the one still-in-progress cycle per schedule. The backend
  // returns these two fields because we always send include_active=true.
  is_active: boolean
  issues_raised: number // TOTAL issues raised in the cycle (all severities)
  severity_breakdown: CompletedSurveySeverity // total raised per severity; sums to issues_raised
  critical: number // OPEN critical only (critical_active) ≤ severity_breakdown.critical
}

// Standard six-key pager, shared across the reports quick-list responses.
export interface ReportsPagination {
  current_page: number
  page_size: number
  total_pages: number
  total_count: number
  has_next: boolean
  has_previous: boolean
}

export interface CompletedSurveysResponse {
  completed_surveys: CompletedSurvey[]
  pagination: ReportsPagination
}

export interface CompletedSurveysRequest {
  entityId?: string | undefined // omit = all visible entities
  page?: number // default 1
  pageSize?: number // default 20 (backend caps at 200)
}

export function listCompletedSurveys(
  req: CompletedSurveysRequest = {},
): Promise<CompletedSurveysResponse> {
  // `include_active=true` is always sent (same convention as getSurveyReport):
  // finished cycles come back unchanged, plus the one still-in-progress cycle
  // per schedule with `is_active: true` so the landing can offer a live report.
  return api.get<CompletedSurveysResponse>('/reports/completed-surveys', {
    params: {
      entity_id: req.entityId || undefined,
      page: req.page ?? 1,
      page_size: req.pageSize ?? 20,
      include_active: true,
    },
  })
}
